/***
 *
 *   PROJECT CATEGORIES (settings)
 *   List / rename / configure / delete ticket categories for a project, plus
 *   create-from and import actions (moved off the tickets-page category rail).
 *
 **********/

import React, { useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Animate,
    Button,
    Card,
    TitleRow,
    ViewContext,
} from 'components/lib';
import { useLiveQuery } from 'components/hooks/websocket';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';
import { CategoryIntegrationActions } from 'components/tickets/categoryIntegrationActions';
import { findActiveTickets } from 'utilities/tickets';

const ProjectCategories = () => {
    const { id: projectId } = useParams();
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const viewContext = useContext(ViewContext);
    const {
        user: { permission },
    } = useContext(AuthContext);

    const categories = useLiveQuery(
        'ticket_categories',
        { project_id: projectId },
        { enabled: !!projectId },
    );

    const tickets = useLiveQuery(
        'tickets',
        { project_id: projectId },
        { enabled: !!projectId },
    );

    const canManage = ['admin', 'owner'].includes(permission);
    const categoryList = categories.data || [];
    const activeTickets = findActiveTickets(tickets.data || []);

    function ticketCount(categoryId) {
        return activeTickets.filter((t) => t.ticket_category_id === categoryId).length;
    }

    function openRename(category) {
        viewContext.modal.show(
            {
                title: _t('categories.rename'),
                form: {
                    name: {
                        label: _t('ticket.category.name'),
                        type: 'text',
                        required: true,
                        value: category.name,
                    },
                },
                buttonText: _t('save'),
                url: `/api/ticket/category/${category.id}`,
                method: 'PATCH',
                injectedData: {
                    project_id: projectId,
                },
            },
            () => {
                categories.refetch();
            },
        );
    }

    function openDelete(category) {
        const count = ticketCount(category.id);
        viewContext.modal.show(
            {
                title: _t('ticket.category.delete'),
                form: {},
                text:
                    count > 0
                        ? `${_t('ticket.category.delete')} "${category.name}" (${count})`
                        : `"${category.name}"`,
                buttonText: _t('delete'),
                url: `/api/ticket/category/${category.id}`,
                method: 'delete',
                destructive: true,
            },
            () => {
                categories.refetch();
            },
        );
    }

    const isLoading = categories.isLoading || categories.status === 'loading';

    return (
        <Animate>
            <Card>
                <section id="ticket-categories" className="scroll-mt-24">
                    <TitleRow title={_t('categories.label.multiple')}>
                        {canManage && (
                            <CategoryIntegrationActions
                                projectId={projectId}
                                onSuccess={() => categories.refetch()}
                            />
                        )}
                    </TitleRow>

                    {isLoading ? (
                        <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
                    ) : !categoryList.length ? (
                        <div className="rounded-lg border border-dashed border-border bg-muted/15 px-4 py-6 text-center text-sm text-text-secondary">
                            {_t('categories.empty')}
                        </div>
                    ) : (
                        <ul className="divide-y divide-border rounded-lg border border-border">
                            {categoryList.map((category) => {
                                const count = ticketCount(category.id);
                                return (
                                    <li
                                        key={category.id}
                                        className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 text-sm">
                                        <div className="min-w-0">
                                            <p className="font-medium text-text-primary">
                                                {category.name}
                                            </p>
                                            <p className="text-xs text-text-muted">
                                                {count}{' '}
                                                {count === 1
                                                    ? _t('ticket.label.one')
                                                    : _t('ticket.label.multiple')}
                                            </p>
                                        </div>
                                        {canManage && (
                                            <div className="flex flex-wrap items-center gap-1">
                                                <Button
                                                    text={_t('categories.rename')}
                                                    variant="ghost"
                                                    size="sm"
                                                    icon="edit-2"
                                                    iconColor="currentColor"
                                                    action={() => openRename(category)}
                                                />
                                                <Button
                                                    text={_t('categories.configure')}
                                                    variant="secondary"
                                                    size="sm"
                                                    action={() =>
                                                        navigate(
                                                            `/app/tickets/category/${category.id}`,
                                                        )
                                                    }
                                                />
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    icon="trash"
                                                    iconColor="currentColor"
                                                    className="text-error hover:bg-error/10"
                                                    ariaLabel={_t('delete')}
                                                    action={() => openDelete(category)}
                                                />
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </Card>
        </Animate>
    );
};

export default ProjectCategories;
